import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { useCatalogPresentationOptional } from 'context/CatalogPresentationContext';
import { useStockAppearancePreview } from 'context/StockAppearancePreviewContext';
import { resolveStoreMediaUrl } from 'utils/themeDisplay';
import type { StockLevel, StockLevelAppearance } from 'types/product';
import { isOverlayBadge, levelAppearance, stockLevelText } from 'utils/stockLevel';

interface StockLevelChipProps {
  level?: StockLevel | null;
  quantity?: number | null;
  unitName?: string | null;
  size?: 'small' | 'medium';
  /** Para vistas previas del admin. */
  look?: StockLevelAppearance;
  /** No mostrar nada si la banda va como cocarda sobre la foto (tarjeta y ficha). */
  hideWhenOverlay?: boolean;
}

export const useStockLevelLook = (level: StockLevel | null | undefined, override?: StockLevelAppearance) => {
  const presentation = useCatalogPresentationOptional();
  const preview = useStockAppearancePreview();
  if (!level) return null;
  return override ?? levelAppearance(preview ?? presentation?.stockAppearance, level);
};

const StockLevelChip: React.FC<StockLevelChipProps> = ({
  level,
  quantity,
  unitName,
  size = 'small',
  look: override,
  hideWhenOverlay = false,
}) => {
  const look = useStockLevelLook(level, override);
  if (!look || !look.visible) return null;
  if (hideWhenOverlay && isOverlayBadge(look)) return null;
  const text = stockLevelText(look, quantity, unitName);

  if (look.display === 'text') {
    return (
      <Typography
        component="span"
        variant={size === 'small' ? 'caption' : 'body2'}
        sx={{ color: look.color, fontWeight: 700, display: 'inline-block' }}
      >
        {text}
      </Typography>
    );
  }

  if (isOverlayBadge(look)) {
    const iconSize = size === 'small' ? 20 : 28;
    return (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
        <Box
          component="img"
          src={resolveStoreMediaUrl(look.imageUrl ?? '')}
          alt=""
          sx={{ width: iconSize, height: iconSize, objectFit: 'contain' }}
        />
        <Typography component="span" variant={size === 'small' ? 'caption' : 'body2'} fontWeight={700}>
          {text}
        </Typography>
      </Box>
    );
  }

  const outlined = look.variant === 'outlined';
  return (
    <Chip
      size={size}
      label={text}
      variant={outlined ? 'outlined' : 'filled'}
      sx={{
        fontWeight: 600,
        alignSelf: 'flex-start',
        ...(outlined
          ? { borderColor: look.color, color: look.textColor, bgcolor: 'transparent' }
          : { bgcolor: look.color, color: look.textColor }),
      }}
    />
  );
};

export default StockLevelChip;
